/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { IS_IOS } from '@config/environment';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import ModalTemplate from '@component/common/modal/modal-template';
import { ETheme } from '@datatype/type';

// 이야기고 전용 — 스토어에 출시된 앱이 이야기고뿐이라 useApplicationVersionCheck가 다른 서비스를 걸러낸다.
// 다른 서비스가 앱을 내면 SERVICE별 URL로 분리해야 한다.
const IYAGIGO_IOS_STORE_URL = 'https://apps.apple.com/app/id6759956246';
const IYAGIGO_ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.iyagigo';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const NoteContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.GRAY_BACKGROUND_COLOR};
`;

const DismissContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

interface IProps {
  // minVersion 미만이면 true — 닫을 수 없다
  isForce: boolean;
  releaseNote?: string | null;
  handleDismiss: () => void;
}

/**
 * 앱 버전이 낮을 때 노출되는 업데이트 모달
 * - 강제(isForce): 닫기 불가 — minVersion 미만이라 정상 동작을 보장할 수 없음
 * - 권장: '다음에'로 닫을 수 있음
 * - 버튼 클릭 시 플랫폼에 맞는 스토어로 이동
 */
const ApplicationUpdateModal: React.FC<IProps> = ({ isForce, releaseNote, handleDismiss }) => {
  // '- 항목' 줄바꿈 구분 → 라인 배열 (빈 줄 제거)
  const noteLines = (releaseNote ?? '')
    .split('\n')
    .map((line) => line.replace(/^-\s*/, '').trim())
    .filter((line) => line.length > 0);

  const handleUpdateClick = () => {
    window.open(IS_IOS ? IYAGIGO_IOS_STORE_URL : IYAGIGO_ANDROID_STORE_URL, '_blank');
  };

  return (
    <ModalTemplate hideCloseButton closeOnBackgroundClick={false}>
      <Wrapper>
        <TextBlock
          text={isForce ? '업데이트가 필요해요' : '새로운 버전이 나왔어요'}
          design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
        />
        <Gap height={'5px'} />
        <TextBlock
          text={isForce ? '최신 버전으로 업데이트하고 이어서 학습해요' : '업데이트하고 새로운 기능을 만나보세요'}
          design={{ text: { size: 'medium', color: color.GRAY_1, bold: false } }}
        />
        {noteLines.length > 0 && (
          <>
            <Gap height={size.MARGIN_1} />
            <NoteContainer>
              {noteLines.map((line) => (
                <TextBlock
                  key={line}
                  text={`· ${line}`}
                  design={{ text: { size: 'small', color: color.GRAY_1, bold: false } }}
                />
              ))}
            </NoteContainer>
          </>
        )}
        <Gap height={size.MARGIN_2} />
        <TextButton
          theme={ETheme.BROWN}
          kind={'filled'}
          size={'medium'}
          shape='pill'
          width={'100%'}
          text={'업데이트'}
          handleClick={handleUpdateClick}
        />
        {!isForce && (
          <>
            <Gap height={size.MARGIN_1} />
            <DismissContainer>
              <TextBlock
                text='다음에'
                design={{
                  text: { size: 'small', color: color.GRAY_1, bold: false, underline: true },
                  box: { event: { cursor: 'pointer' } },
                }}
                click={{ handler: handleDismiss }}
              />
            </DismissContainer>
          </>
        )}
      </Wrapper>
    </ModalTemplate>
  );
};

export default ApplicationUpdateModal;
